/**
 * What the devotee would get back if they cancelled now (CSR-169, doc 17 §2.3 mode 1).
 *
 * A read of `GET /v1/admin/bookings/{code}/cancellation-quote`, which charges the windows of the
 * policy version the booking pinned at payment — not the version in force today, which is what
 * the policy editor shows. Support reads it on a call; nothing here raises the cancel, because
 * cancelling on the devotee's behalf is the owner's question, not a console button.
 *
 * The M-row chip carries the matrix's `who` and `when` only: its other columns are worked examples
 * with their own amounts, and beside this booking's real figures they would read as a second quote.
 */
import { KV, PriceBreakdown, type KVRow } from '../components/entities';
import { ErrorState, LoadingState } from '../components/feedback';
import { Section } from '../components/kit';
import { StatusChip } from '../components/primitives';
import { errorMessage } from '../lib/api/client';
import { useAdminCancellationQuote, type AdminCancellationQuote } from '../lib/api/hooks/bookings';
import { formatInr } from '../lib/money';
import { REFUND_MATRIX } from './RefundMatrix';

const UNPAID = 'Nothing was paid — cancellation is free; no refund object will be created.';
const LATE =
  'The pandit is past the grace window (D37): the devotee gets everything back, and the platform bears Razorpay’s fee.';
const FEE_BORNE = 'Razorpay keeps its fee on the refunded payment; the platform bears it.';
const FEE_FUNDED =
  'Razorpay keeps its fee on the refunded payment; the platform bears it, out of the fee and commission it keeps.';

/** Who bears the gateway fee, per backend 28 §5.3. A row the table does not name says nothing. */
const FEE_BEARER: Readonly<Record<string, string>> = { M2: FEE_BORNE, M3: FEE_FUNDED, M4: FEE_FUNDED, M5: FEE_FUNDED };

export function matrixLabel(id: string): string {
  const row = REFUND_MATRIX.find((r) => r.id === id);
  return row ? `${id} · ${row.who}, ${row.when}` : id;
}

const component = (key: string) => key.charAt(0).toUpperCase() + key.slice(1).replaceAll('_', ' ');
const percent = (fraction: string) => `${Number((Number(fraction) * 100).toFixed(2))}%`;

function feeNote(quote: AdminCancellationQuote): string | undefined {
  if (quote.pandit_late) return LATE;
  return quote.matrix_row ? FEE_BEARER[quote.matrix_row] : undefined;
}

export function QuoteBody({ quote }: { quote: AdminCancellationQuote }) {
  const rows: KVRow[] = [];
  if (quote.policy_name) rows.push(['Policy pinned', `${quote.policy_name} v${quote.policy_version}`]);
  rows.push(['Before the start', `${Number(quote.hours_before_start).toFixed(1)} h`]);
  if (quote.matrix_row) rows.push(['Matrix row', <StatusChip label={matrixLabel(quote.matrix_row)} />]);

  return (
    <>
      <KV rows={rows} />
      {quote.unpaid ? (
        <p>{UNPAID}</p>
      ) : !quote.cancellable ? (
        <p>
          Not cancellable now{quote.not_cancellable_reason ? ` — ${quote.not_cancellable_reason}` : ''}.
        </p>
      ) : (
        <PriceBreakdown
          variant="admin"
          lines={quote.lines.map((line) => ({
            label: `${component(line.component)} · ${percent(line.fraction)} of ${formatInr(line.base_paise, { paise: true })}`,
            amount: formatInr(line.refund_paise, { paise: true }),
          }))}
          total={formatInr(quote.total_refund_paise, { paise: true })}
          footnote={feeNote(quote)}
        />
      )}
    </>
  );
}

export function CancellationQuotePanel({ code }: { code: string }) {
  const quote = useAdminCancellationQuote(code);
  return (
    <Section
      title="If the devotee cancelled now"
      note="Under the policy version this booking pinned at payment, not the one in force today."
      style={{ marginTop: 16 }}
    >
      {quote.isPending && <LoadingState message="Reading the quote…" />}
      {quote.isError && <ErrorState title="Could not read the quote" cause={errorMessage(quote.error)} />}
      {quote.isSuccess && <QuoteBody quote={quote.data} />}
    </Section>
  );
}
